/**
 * LookupField, shared "this field points to another record" affordance.
 *
 * Per D-114 record-page discipline: when a record references a foreign key
 * to another record (e.g., a Tool record references its Connector, a Dispatch
 * record references its User + Tool), the field renders as a styled link with
 * icon + name + hover state. Never raw text.
 *
 * Drops into the existing .lightning-field-grid scaffolding so it sits next
 * to other lightning-field rows on any record-page detail tab.
 *
 * Example:
 *   <LookupField
 *     label="Connector"
 *     to={`/connectors/${connectorSlug}`}
 *     name="Gmail"
 *     icon={<ConnectorLogo slug="gmail" name="Gmail" size="sm" />}
 *   />
 *
 * `to` renders the app's injected link component (see UiLink.tsx) rather than
 * react-router's `Link`, so this component carries no router dependency.
 */

import type { ReactNode } from 'react';
import { useUiLink } from './UiLink.tsx';

interface LookupFieldProps {
  /** Field label (e.g., "Connector", "User", "Tool"). */
  label: string;
  /** Target route for the linked record. */
  to: string;
  /** Display name of the linked record. */
  name: string;
  /** Optional small icon rendered before the name (logo, avatar, etc.). */
  icon?: ReactNode;
}

export function LookupField({ label, to, name, icon }: LookupFieldProps) {
  const Link = useUiLink();
  return (
    <div className="lightning-field">
      <span className="lightning-field-label">{label}</span>
      <Link to={to} className="lookup-field-link">
        {icon !== undefined && (
          <span className="lookup-field-icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <span className="lookup-field-name">{name}</span>
      </Link>
    </div>
  );
}
